import { yearRange } from "src/utils";
import Table from "../Calculators/Versatile/Table";
import {
  CalculatorSettings,
  StoredCalculator,
  calculateProjection,
  getReturns,
} from "../Calculators/Versatile/versatileTypes";
import VersatileBalance from "../Charts/VersatileBalance";
import { PrintClient } from "src/types";
import config from "src/services/config";
import Input from "../Inputs/Input";
import Select from "../Inputs/Select";

interface VersatileCalculatorProps {
  settings: StoredCalculator;
  client: PrintClient;
}

const AnnuityRorReport = ({ client, settings }: VersatileCalculatorProps) => {
  const returnsMemo = getReturns(settings);
  const calculations = calculateProjection(settings, returnsMemo);
  const getRandom = (
    settings: CalculatorSettings,
    type: "best" | "mean" | "worst" | "25th" | "75th",
  ) => {
    const sett = {
      ...settings,
      returns: {
        ...settings.returns,
        selectedRandom: type as any,
      },
    };
    const s = getReturns(sett);
    return {
      data: calculateProjection(sett, s),
      returns: yearRange(1, settings.user.endYear).map((i) => s(i)),
    };
  };

  const chartData =
    settings.returns.returnType === "random"
      ? [
          {
            label: "Worst",
            ...getRandom(settings, "worst"),
            color: "#e74c3c", // Indigo color
          },
          {
            label: "25th",
            ...getRandom(settings, "25th"),
            color: "#ff8614", // Indigo color
          },
          {
            label: "Median",
            ...getRandom(settings, "mean"),
            color: "#46C6FF", // Indigo color
          },
          {
            label: "75th",
            ...getRandom(settings, "75th"),
            color: "#4693FF", // Indigo color
          },
          {
            label: "Best",
            ...getRandom(settings, "best"),
            color: "#2ecc71", // Indigo color
          },
        ]
      : [
          {
            label: "Balance",
            data: calculations,
            returns: yearRange(1, settings.user.endYear).map((i) =>
              returnsMemo(i),
            ),
            color: "#3498db", // Indigo color
          },
        ];
  return (
    <div className="px-10">
      <div className="flex">
        <div className="">
          <div className="flex flex-col gap-6">
            <div className="flex gap-3 items-center mb-9">
              <div>
                <img
                  src={
                    client?.userdata?.logo
                      ? `${config.PRINT_API_URL}logo?logo=${client?.userdata?.logo}`
                      : "/img/logo.png"
                  }
                  alt="logo"
                  className="h-16"
                />
              </div>
              <h1 className="font-light text-3xl">
                Annuity RoR Report — {settings.name || "Live"}
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className={`flex  gap-6 justify-between mb-6`}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 border p-4 rounded-lg shadow-md print:shadow-none bg-white">
            <div className="col-span-3">
              <h2 className="text-xl font-semibold">User Settings</h2>
            </div>
            <div className="flex flex-col gap-3 justify-between">
              <div className="flex flex-col gap-3 min-w-[400px]">
                <Input
                  labelLength={100}
                  width="w-[130px]!"
                  label="Initial Premium"
                  subtype="money"
                  value={
                    settings.user.presentValue !== null
                      ? Math.round(settings.user.presentValue)
                      : null
                  }
                  setValue={() => {}}
                />{" "}
              </div>
              <div className="flex flex-col gap-3">
                <Input
                  label="Total Years"
                  subtype="number"
                  labelLength={100}
                  width="w-[50px]!"
                  value={settings.user.endYear}
                  setValue={() => {}}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4 border p-4 rounded-lg shadow-md print:shadow-none bg-white">
            <div className="flex justify-between items-center w-full">
              <h2 className="text-xl font-semibold">Return</h2>
              <div className="flex flex-col gap-6 ">
                <div className="flex gap-2 w-[180px]">
                  <Input
                    label="Return (%)"
                    labelLength={80}
                    subtype="percent"
                    value={
                      Math.round(settings.returns.rateOfReturn * 100) / 100
                    }
                    setValue={() => {}}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4 border p-4 rounded-lg shadow-md print:shadow-none bg-white">
          <div className="flex gap-7 justify-between items-center">
            <h2 className="text-xl font-semibold">Income</h2>
          </div>

          {settings.payment.type === "simple" && (
            <div className="flex gap-3 flex-col">
              <div className="flex gap-2 w-[241px]">
                <Input
                  labelLength={130}
                  label="Payment ($)"
                  subtype="money"
                  size="md"
                  width="w-[106px]!"
                  value={
                    settings.payment.amount !== null
                      ? Math.round(settings.payment.amount)
                      : null
                  }
                  setValue={() => {}}
                />
              </div>
              <Input
                label="Years of Deferral"
                subtype="number"
                labelLength={130}
                width="w-[80px]!"
                value={settings.payment.startYear}
                setValue={() => {}}
              />
            </div>
          )}
          <div className="flex gap-3 flex-col">
            <Input
              label="Increase (%)"
              labelLength={130}
              width="w-[80px]!"
              subtype="percent"
              value={settings.payment.increase}
              setValue={() => {}}
            />
            <div className="w-[212px]">
              <Select
                labelLength={130}
                label="Timing"
                width="w-[80px]!"
                options={[
                  { id: "beginning", name: "BoY" },
                  { id: "end", name: "EoY" },
                ]}
                selected={{
                  id: settings.payment.timing,
                  name: settings.payment.timing === "beginning" ? "BoY" : "EoY",
                }}
                setSelected={() => {}}
              />
            </div>
          </div>
        </div>
      </div>
      <div
        className={` transition-maxHeight w-full duration-500 ease-in-out ${true ? "max-h-[1500px]" : "max-h-0 overflow-hidden"}`}
      >
        <VersatileBalance datasets={chartData} print={true} prefix="main" />
      </div>
      <div className="break-after-page"></div>

      <Table
        calculations={calculations}
        returnsMemo={returnsMemo}
        open={true}
        simple
      />
    </div>
  );
};

export default AnnuityRorReport;
